import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useOrganization } from '../contexts/OrganizationContext'
import { supabase } from '../lib/supabase'
// Removed ChevronDownIcon import - using simple dropdown

interface Organization {
  id: string
  name: string
  subdomain: string | null
}

export function OrganizationSwitcher() {
  const { isSuperAdmin } = useAuth()
  const { organization, refreshOrganization: _refreshOrganization } = useOrganization()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(false)

  // Load organizations on mount - must be before any early returns
  useEffect(() => {
    if (isSuperAdmin) {
      loadOrganizations()
    }
  }, [isSuperAdmin])

  // Only render for super admins
  if (!isSuperAdmin) return null

  const loadOrganizations = async () => {
    const { data, error } = await supabase
      .from('organizations')
      .select('id, name, subdomain')
      .order('name')

    if (!error && data) {
      setOrganizations(data)
    }
  }

  const switchOrganization = async (orgId: string) => {
    // Check if already in the selected state
    if (orgId === 'platform' && !organization) {
      return
    }
    if (orgId === organization?.id) {
      return
    }

    setLoading(true)
    
    try {
      // Special handling for platform mode
      if (orgId === 'platform') {
        console.log('Switching to platform mode...')
        
        // Clear all organization-related storage
        localStorage.removeItem('locked-organization-id')
        localStorage.removeItem('locked-organization-timestamp')
        localStorage.removeItem('selectedOrganizationId')
        
        // Set platform mode
        localStorage.setItem('super-admin-mode', 'platform')
        
        // Navigate to platform pages
        window.location.href = '/platform/organizations'
        return
      }
      
      // Set organization mode
      localStorage.setItem('super-admin-mode', 'organization')
      // For super admins, we need to call a special function to switch organizations
      const { data, error } = await supabase.rpc('switch_organization_for_super_admin', {
        p_organization_id: orgId
      })
      
      if (error) {
        console.error('Failed to switch organization:', error)
        alert('Failed to switch organization. Please try again.')
        setLoading(false)
        return
      }
      
      if (!data?.success) {
        alert(data?.error || 'Failed to switch organization')
        setLoading(false)
        return
      }
      
      // Clear the organization lock for super admins
      localStorage.removeItem('locked-organization-id')
      localStorage.removeItem('locked-organization-timestamp')
      
      // Store the selected organization
      localStorage.setItem('selectedOrganizationId', orgId)
      
      // Force a full page reload to refresh the JWT and all context
      window.location.reload()
    } catch (err) {
      console.error('Error switching organization:', err)
      alert('An error occurred while switching organizations')
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <select
        value={organization?.id || 'platform'}
        onChange={(e) => e.target.value && switchOrganization(e.target.value)}
        className="form-select form-input pr-8 cursor-pointer"
        disabled={loading}
        style={{ minWidth: '200px' }}
      >
        {!organization && <option value="">Select Organization</option>}
        <option value="platform">🌐 Platform Admin</option>
        <optgroup label="Organizations">
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </optgroup>
      </select>
    </div>
  )
}